import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import MergeChatLogo from './MergeChatLogo';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/th';
dayjs.extend(relativeTime);
dayjs.locale('th');

function Avatar({ name = '', color = '#185FA5', pictureUrl = null, size = 36 }) {
  if (pictureUrl) {
    return (
      <div className="avatar" style={{ width: size, height: size }}>
        <img src={pictureUrl} alt={name} className="avatar-img" />
      </div>
    );
  }
  const initials = name.trim().slice(0, 2) || '??';
  const bg = hexToSoft(color);
  return (
    <div className="avatar" style={{ width: size, height: size, background: bg, color }}>
      {initials}
    </div>
  );
}

function hexToSoft(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},0.12)`;
}

const STATUS_TABS = [
  { key: 'open',    label: 'เปิด',     color: '#16a34a' },
  { key: 'pending', label: 'รอ',       color: '#ea580c' },
  { key: 'closed',  label: 'ปิดแล้ว', color: '#94a3b8' },
  { key: 'spam',    label: '🚫 สแปม',  color: '#dc2626' },
];

export default function ConversationList({
  conversations = [],
  oaAccounts = [],
  activeId,
  onSelect,
  filter,
  onFilterChange,
  statusFilter = 'open',
  onStatusFilterChange,
  onStatusChange,
}) {
  const [search, setSearch]   = useState('');
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, conv }
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    let list = conversations.filter(c => c.status === statusFilter);
    if (filter !== 'all') list = list.filter(c => c.oa_id === Number(filter));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        c =>
          c.display_name?.toLowerCase().includes(q) ||
          c.last_message?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [conversations, filter, statusFilter, search]);

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  return (
    <aside className="sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <MergeChatLogo size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sidebar-title"><span>Merge</span><span style={{color:'#FF6347'}}>Chat</span></div>
            <div className="sidebar-sub">{totalUnread > 0 ? `${totalUnread} ข้อความใหม่` : 'ไม่มีข้อความใหม่'}</div>
          </div>
          <div className="sidebar-nav-btns">
            <button className="btn-nav-icon" onClick={() => navigate('/dashboard')} title="Dashboard">📊</button>
            <button className="btn-nav-icon" onClick={() => navigate('/settings')} title="ตั้งค่า">⚙️</button>
          </div>
        </div>
      </div>

      {/* Status tabs */}
      <div className="status-tabs">
        {STATUS_TABS.map(({ key, label, color }) => {
          const count = conversations.filter(c => c.status === key).length;
          const isActive = statusFilter === key;
          return (
            <button
              key={key}
              className={`status-tab ${isActive ? 'active' : ''}`}
              style={isActive ? { borderBottomColor: color, color } : {}}
              onClick={() => onStatusFilterChange(key)}
            >
              {label}
              {count > 0 && (
                <span className="status-tab-count" style={isActive ? { background: color, color: '#fff' } : {}}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* OA filter chips */}
      <div className="oa-filter">
        <button
          className={`oa-chip ${filter === 'all' ? 'active' : ''}`}
          onClick={() => onFilterChange('all')}
        >
          ทั้งหมด
          {totalUnread > 0 && <span className="chip-badge">{totalUnread}</span>}
        </button>
        {oaAccounts.map(oa => {
          const unread = conversations
            .filter(c => c.oa_id === oa.id)
            .reduce((s, c) => s + (c.unread_count || 0), 0);
          return (
            <button
              key={oa.id}
              className={`oa-chip ${filter === String(oa.id) ? 'active' : ''}`}
              style={filter === String(oa.id) ? { background: oa.color, borderColor: oa.color, color: '#fff' } : { borderColor: oa.color + '66', color: oa.color }}
              onClick={() => onFilterChange(String(oa.id))}
            >
              {oa.name}
              {unread > 0 && <span className="chip-badge" style={{ background: oa.color }}>{unread}</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="search-wrap">
        <span className="search-icon">⌕</span>
        <input
          className="search-input"
          placeholder="ค้นหาแชท..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* List */}
      <div className="chat-list">
        {filtered.length === 0 && (
          <div className="empty-state">ไม่มีแชท</div>
        )}
        {filtered.map(conv => {
          const oa = oaAccounts.find(o => o.id === conv.oa_id);
          const isActive = conv.id === activeId;
          const hasUnread = conv.unread_count > 0;
          return (
            <div
              key={conv.id}
              className={`chat-item ${isActive ? 'active' : ''} ${hasUnread ? 'unread' : ''}`}
              onClick={() => onSelect(conv)}
              onContextMenu={e => {
                e.preventDefault();
                setCtxMenu({ x: e.clientX, y: e.clientY, conv });
              }}
            >
              <Avatar name={conv.display_name} color={oa?.color || '#185FA5'} pictureUrl={conv.picture_url} />
              <div className="chat-info">
                <div className="chat-name">{conv.display_name || 'ผู้ใช้ไม่ระบุชื่อ'}</div>
                <div className="chat-preview">{conv.last_message || '—'}</div>
                <div className="chat-badges">
                  {oa && (
                    <span className="oa-tag" style={{ background: hexToSoft(oa.color), color: oa.color }}>
                      {oa.name}
                    </span>
                  )}
                  {(conv.tags || []).slice(0, 2).map(tag => (
                    <span key={tag.id} className="tag-pill-sm" style={{ background: tag.color + '22', color: tag.color }}>
                      {tag.name}
                    </span>
                  ))}
                  {(conv.tags || []).length > 2 && (
                    <span className="tag-pill-sm tag-pill-more">+{conv.tags.length - 2}</span>
                  )}
                </div>
              </div>
              <div className="chat-meta">
                <span className="chat-time">
                  {conv.last_message_at ? dayjs(conv.last_message_at).fromNow(true) : ''}
                </span>
                {hasUnread && (
                  <span className="unread-badge">{conv.unread_count}</span>
                )}
                <span className={`status-dot status-${conv.status}`} title={conv.status} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Right-click context menu */}
      {ctxMenu && (
        <>
          <div className="ctx-overlay" onClick={() => setCtxMenu(null)} />
          <div className="ctx-menu" style={{ top: ctxMenu.y, left: ctxMenu.x }}>
            {ctxMenu.conv.status !== 'closed' && (
              <button onClick={() => { onStatusChange?.(ctxMenu.conv.id, 'closed'); setCtxMenu(null); }}>
                ✓ ปิดเคส
              </button>
            )}
            {ctxMenu.conv.status === 'open' && (
              <button onClick={() => { onStatusChange?.(ctxMenu.conv.id, 'pending'); setCtxMenu(null); }}>
                ⏸ รอดำเนินการ
              </button>
            )}
            {ctxMenu.conv.status !== 'open' && ctxMenu.conv.status !== 'spam' && (
              <button onClick={() => { onStatusChange?.(ctxMenu.conv.id, 'open'); setCtxMenu(null); }}>
                ↩ เปิดใหม่
              </button>
            )}
            {ctxMenu.conv.status !== 'spam' && (
              <button className="ctx-danger" onClick={() => { onStatusChange?.(ctxMenu.conv.id, 'spam'); setCtxMenu(null); }}>
                🚫 ทำเครื่องหมายสแปม
              </button>
            )}
            {ctxMenu.conv.status === 'spam' && (
              <button onClick={() => { onStatusChange?.(ctxMenu.conv.id, 'open'); setCtxMenu(null); }}>
                ↩ ยกเลิกสแปม
              </button>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
